import { LoadingButton } from '@mui/lab';
import {
  Divider,
  FormControl,
  InputLabel,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import storeConnector from '@src/store/storeConnector';
import { CalendarEvent } from '@src/types/Events';
import { Stack as FracStack } from '@src/types/Stacks';
import { Dispatch, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { colorOptions, StyledFormSection } from './FormSections/_elements';
import CommandsStackSection from './FormSections/CommandsStackSection';
import DateSection from './FormSections/DateSection';
import { generateValues } from './FormSections/generateValues';
import { eventFormStyles } from './styles';
import { normalizeEventForSubmission } from './utils';

interface AddEditEventFormProps {
  activeEvent: CalendarEvent;
  stacks: FracStack[];
  setDialogOpen: Dispatch<React.SetStateAction<boolean>>;
  setConfirmOpen: Dispatch<React.SetStateAction<boolean>>;
  handleEventUpdates: (eventToSubmit: CalendarEvent) => void;
  isMutating?: boolean;
}

function AddEditEventForm({
  activeEvent,
  stacks,
  handleEventUpdates,
  isMutating = false,
}: AddEditEventFormProps) {
  const theme = useTheme();
  const {
    reset,
    control,
    handleSubmit,
    formState: { errors },
  } = useFormContext<CalendarEvent>();

  useEffect(() => {
    reset(generateValues(activeEvent));
  }, [activeEvent, reset]);

  const onSubmit = (data: CalendarEvent) => {
    const eventToSubmit = normalizeEventForSubmission(data);
    return handleEventUpdates(eventToSubmit);
  };

  return (
    <>
      <Stack
        spacing={3}
        flex={1}
        divider={<Divider sx={eventFormStyles.divider(theme)} />}
      >
        <StyledFormSection>
          <Typography variant='h6' sx={eventFormStyles.sectionTitle}>
            General
          </Typography>
          <Stack direction='row' spacing={2} sx={eventFormStyles.sectionStack}>
            <Controller
              name='title'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  sx={eventFormStyles.titleField}
                  label='Event Title'
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  fullWidth
                />
              )}
            />

            <Controller
              name='backgroundColor'
              control={control}
              render={({ field }) => (
                <FormControl fullWidth sx={eventFormStyles.colorField}>
                  <InputLabel id='select-bgcolor'>Background Color</InputLabel>
                  <Select
                    {...field}
                    error={!!errors.backgroundColor}
                    labelId='select-bgcolor'
                    label='Background Color'
                  >
                    {colorOptions}
                  </Select>
                </FormControl>
              )}
            />
          </Stack>
        </StyledFormSection>

        <DateSection />

        <CommandsStackSection stacks={stacks} />
      </Stack>
      <Divider sx={eventFormStyles.divider(theme)} />
      <StyledFormSection sx={eventFormStyles.submitSection}>
        <LoadingButton
          variant='contained'
          size='large'
          disabled={Object.keys(errors).length > 0}
          loading={isMutating}
          onClick={handleSubmit(onSubmit)}
          sx={eventFormStyles.submitButton}
        >
          Save
        </LoadingButton>
      </StyledFormSection>
    </>
  );
}

export default storeConnector(AddEditEventForm, {
  config: ['siteMeta'],
  user: ['username', 'role'],
});
